import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Package, Truck, Home } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../components/ProductCard";

const STEP_ICONS = [Check, Package, Truck, Home];
const STATUS_PROGRESS = { pending: 0, confirmed: 1, shipped: 3, delivered: 4 };

function OrderTimeline({ status, t }) {
  if (status === "cancelled") {
    return (
      <div data-testid="order-timeline-cancelled" className="mt-6 pt-5 border-t border-white/10 text-sm text-red-300">
        {t("orders.track.cancelled_note")}
      </div>
    );
  }
  const steps = [
    t("orders.track.step_confirmed"),
    t("orders.track.step_preparing"),
    t("orders.track.step_shipped"),
    t("orders.track.step_delivered"),
  ];
  const progress = STATUS_PROGRESS[status] ?? 0;
  return (
    <div data-testid="order-timeline" className="mt-6 pt-6 border-t border-white/10">
      <div className="overline text-[10px] mb-6">{t("orders.track.title")}</div>
      <div className="flex items-start">
        {steps.map((label, i) => {
          const done = i < progress;
          const current = i === progress && progress < 4;
          const Icon = STEP_ICONS[i];
          return (
            <div key={label} className="flex-1 flex flex-col items-center relative">
              {i > 0 && (
                <div
                  className={`absolute top-4 right-1/2 w-full h-px ${i <= progress ? "bg-[#D4AF37]" : "bg-white/10"}`}
                  style={{ zIndex: 0 }}
                />
              )}
              <div
                className={`relative z-10 w-8 h-8 flex items-center justify-center border transition-colors ${
                  done
                    ? "border-[#D4AF37] bg-[#D4AF37] text-[#0C0C0A]"
                    : current
                    ? "border-[#D4AF37] text-[#D4AF37] bg-[#151516] animate-pulse"
                    : "border-white/15 text-[#5a5a54] bg-[#151516]"
                }`}
              >
                <Icon size={14} strokeWidth={1.5} />
              </div>
              <div
                className={`mt-3 text-[10px] uppercase tracking-[0.18em] text-center px-1 ${
                  done || current ? "text-[#D4AF37]" : "text-[#5a5a54]"
                }`}
              >
                {label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatusPill({ status, t }) {
  const color = {
    pending: "text-[#A3A39D] border-white/20",
    confirmed: "text-[#D4AF37] border-[#D4AF37]/40",
    shipped: "text-blue-300 border-blue-300/40",
    delivered: "text-green-300 border-green-300/40",
    cancelled: "text-red-300 border-red-300/40",
  }[status] || "text-[#A3A39D] border-white/20";
  return (
    <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-[0.2em] border ${color}`}>
      {t(`orders.status.${status}`)}
    </span>
  );
}

export default function MyOrders() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (user === false) return;
    if (!user) return;
    api.get("/orders/me").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  if (user === null) {
    return <div className="pt-40 pb-24 max-w-[1500px] mx-auto px-6 md:px-12 text-[#A3A39D]">{t("catalog.loading")}</div>;
  }
  if (user === false) {
    return (
      <div className="pt-40 pb-24 max-w-[1500px] mx-auto px-6 md:px-12">
        <p className="text-[#A3A39D] mb-6">{t("auth.login_title")}.</p>
        <Link to="/login" className="btn-primary">{t("auth.login_cta")}</Link>
      </div>
    );
  }

  const fmtDate = (iso) => new Date(iso).toLocaleDateString(i18n.language, { day: "2-digit", month: "long", year: "numeric" });

  return (
    <div data-testid="my-orders-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="overline mb-5">{t("orders.overline")}</div>
        <h1 className="font-serif text-5xl md:text-7xl text-[#F5F5F0] leading-[0.95] mb-16">
          {t("orders.title")}
        </h1>

        {orders === null ? (
          <div className="text-[#A3A39D] text-sm">{t("catalog.loading")}</div>
        ) : orders.length === 0 ? (
          <div className="border-y border-white/10 py-20 text-center">
            <p className="text-[#A3A39D] mb-8">{t("orders.empty")}</p>
            <Link to="/boutique" className="btn-primary">{t("orders.browse_shop")}</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="bg-[#151516] border border-white/10 p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                  <div>
                    <div className="overline text-[10px]">{t("orders.order_nb", { id: o.id.slice(0, 8).toUpperCase() })}</div>
                    <div className="text-xs text-[#A3A39D] mt-1">{t("orders.placed_on", { date: fmtDate(o.created_at) })}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <StatusPill status={o.status || "pending"} t={t} />
                    <span className="font-serif text-2xl text-[#D4AF37]">{formatPrice(o.total)}</span>
                  </div>
                </div>
                <div className="text-sm text-[#A3A39D]">
                  {o.items.map((i, idx) => (
                    <span key={idx}>{i.name} × {i.quantity}{idx < o.items.length - 1 ? " · " : ""}</span>
                  ))}
                </div>
                <OrderTimeline status={o.status || "pending"} t={t} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
